import React, { useEffect, useState } from "react";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocationCityIcon from "@mui/icons-material/LocationCity";

const Landing = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMuzukuruDomain, setIsMuzukuruDomain] = useState(false);

  useEffect(() => {
    // Smooth scrolling for navigation links
    setIsMuzukuruDomain(window.location.hostname === "muzukuru.com");
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener("click", (e) => {
        e.preventDefault();
        const href = anchor.getAttribute("href");
        if (href) {
          const target = document.querySelector(href);
          if (target) {
            target.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }
        // Close mobile menu after clicking a link
        setIsMobileMenuOpen(false);
      });
    });

    // Fade in animation on scroll
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
        }
      });
    }, observerOptions);

    document.querySelectorAll(".fade-in").forEach((el) => {
      observer.observe(el);
    });

    // Navbar background on scroll
    const handleScroll = () => {
      const navbar = document.querySelector(".navbar") as HTMLElement;
      if (navbar) {
        if (window.scrollY > 100) {
          navbar.style.background = "rgba(255, 255, 255, 0.98)";
          navbar.style.boxShadow = "0 2px 20px rgba(0,0,0,0.1)";
        } else {
          navbar.style.background = "rgba(255, 255, 255, 0.95)";
          navbar.style.boxShadow = "none";
        }
      }
    };
    window.addEventListener("scroll", handleScroll);

    // Add hover effects to buttons
    document.querySelectorAll(".btn").forEach((btn) => {
      const button = btn as HTMLElement;
      button.addEventListener("mouseenter", () => {
        button.style.transform = "translateY(-2px)";
      });

      button.addEventListener("mouseleave", () => {
        button.style.transform = "translateY(0)";
      });
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Simulate form submission
    alert("Thank you for your message. We will get back to you soon.");
    event.currentTarget.reset();
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            overflow-x: hidden;
        }

        /* Navigation */
        .navbar {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            padding: 1rem 0;
            z-index: 1000;
            transition: all 0.3s ease;
        }

        .nav-container {
            max-width: 1200px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 2rem;
        }

        .logo {
            font-size: 1.8rem;
            font-weight: 700;
            color: #614338;
            text-decoration: none;
            letter-spacing: 0.5px;
        }

        .nav-menu {
            display: flex;
            list-style: none;
            gap: 2rem;
        }

        .nav-menu a {
            text-decoration: none;
            color: #333;
            font-weight: 500;
            transition: color 0.3s ease;
        }

        .nav-menu a:hover {
            color: #614338;
        }

        .nav-auth {
            display: flex;
            gap: 1rem;
        }

        .nav-auth .btn-outline {
            background: transparent;
            color: #614338;
            border: 2px solid #614338;
        }

        .nav-auth .btn-outline:hover {
            background: #614338;
            color: white;
        }

        .mobile-menu-btn {
            display: none;
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: #333;
            z-index: 1001;
        }

        .mobile-menu {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(255, 255, 255, 0.98);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            transform: translateX(100%);
            transition: transform 0.3s ease;
            z-index: 1000;
        }

        .mobile-menu.open {
            transform: translateX(0);
        }

        .mobile-menu ul {
            list-style: none;
            text-align: center;
            margin-bottom: 2rem;
        }

        .mobile-menu li {
            margin: 1.5rem 0;
        }

        .mobile-menu a {
            font-size: 1.5rem;
            color: #333;
            text-decoration: none;
            font-weight: 500;
            transition: color 0.3s ease;
        }

        .mobile-menu a:hover {
            color: #614338;
        }

        .mobile-menu .nav-auth {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        /* Hero Section */
        .hero {
            min-height: 100vh;
            background: linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), 
                        url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 600"><rect fill="%23333" width="1000" height="600"/><path fill="%23555" d="M0 400c100-50 200-100 300-80s200 80 300 60 200-60 300-40s200 60 300 40v220H0z"/></svg>');
            background-size: cover;
            background-position: center;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            color: white;
            position: relative;
        }

        .hero-content {
            max-width: 800px;
            padding: 0 2rem;
            animation: fadeInUp 1s ease-out;
        }

        .hero h1 {
            font-size: 4rem;
            font-weight: 300;
            margin-bottom: 1rem;
            letter-spacing: 3px;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }

        .hero h2 {
            font-size: 1.8rem;
            font-weight: 400;
            margin-bottom: 2rem;
            opacity: 0.9;
        }

        .hero p {
            font-size: 1.2rem;
            margin-bottom: 3rem;
            opacity: 0.8;
            line-height: 1.8;
        }

        .hero-buttons {
            display: flex;
            gap: 2rem;
            justify-content: center;
            flex-wrap: wrap;
        }

        .btn {
            padding: 1rem 2.5rem;
            border: none;
            border-radius: 50px;
            font-size: 1.1rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
        }

        .btn-primary {
            background: #614338;
            color: white;
        }

        .btn-primary:hover {
            background: #A0522D;
            transform: translateY(-2px);
            box-shadow: 0 10px 20px rgba(139, 69, 19, 0.3);
        }

        .btn-outline {
            background: transparent;
            color: white;
            border: 2px solid white;
        }

        .btn-outline:hover {
            background: rgba(255, 255, 255, 0.1);
            transform: translateY(-2px);
        }

        /* Section Styles */
        .section {
            padding: 6rem 0;
        }

        .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 2rem;
        }

        .section-title {
            text-align: center;
            margin-bottom: 3rem;
        }

        .section-title h2 {
            font-size: 3rem;
            font-weight: 300;
            margin-bottom: 1rem;
        }

        .section-title p {
            font-size: 1.2rem;
            color: #666;
            max-width: 600px;
            margin: 0 auto;
        }

        /* Services Grid */
        .services-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 3rem;
            margin-top: 4rem;
        }

        .service-card {
            background: white;
            padding: 3rem 2rem;
            border-radius: 20px;
            text-align: center;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
            border: 1px solid #eee;
            transition: all 0.3s ease;
            height: 100%;
        }

        .service-card:hover {
            transform: translateY(-10px);
            box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }

        .service-icon {
            width: 80px;
            height: 80px;
            background: linear-gradient(45deg, #614338, #A0522D);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 2rem;
            font-size: 2rem;
            color: white;
        }

        .service-card h3 {
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 1rem;
            color: #333;
        }

        .service-card p {
            color: #666;
            line-height: 1.6;
        }

        /* About Section */
        .about {
            background: #f8f8f8;
        }

        .about-content {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 4rem;
            align-items: center;
        }

        .about-text h2 {
            font-size: 3rem;
            font-weight: 300;
            margin-bottom: 2rem;
            color: #333;
        }

        .about-text p {
            font-size: 1.1rem;
            color: #666;
            margin-bottom: 1.5rem;
            line-height: 1.8;
        }

        .about-image {
            width: 100%;
            height: 400px;
            border-radius: 20px;
            background: linear-gradient(135deg, #614338 0%, #A0522D 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            text-align: center;
            font-size: 2rem;
            font-weight: 300;
        }

        /* Testimonials */
        .testimonials-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 3rem;
            margin-top: 4rem;
        }

        .testimonial-card {
            background: white;
            padding: 2.5rem;
            border-radius: 20px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
            border: 1px solid #eee;
            position: relative;
        }

        .stars {
            color: #FFD700;
            font-size: 1.2rem;
            margin-bottom: 1rem;
        }

        .testimonial-text {
            font-style: italic;
            color: #666;
            margin-bottom: 1.5rem;
            line-height: 1.6;
        }

        .testimonial-author {
            font-weight: 600;
            color: #333;
        }

        /* Contact Section */
        .contact {
            background: #333;
            color: white;
        }

        .contact-content {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 4rem;
        }

        .contact-info h2 {
            font-size: 3rem;
            font-weight: 300;
            margin-bottom: 2rem;
        }

        .contact-info p {
            font-size: 1.1rem;
            margin-bottom: 2rem;
            opacity: 0.9;
            line-height: 1.8;
        }

        .contact-details {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }

        .contact-item {
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .contact-icon {
            width: 40px;
            height: 40px;
            background: #614338;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
        }

        .contact-form {
            background: white;
            padding: 3rem;
            border-radius: 20px;
            color: #333;
        }

        .contact-form h3 {
            margin-bottom: 2rem;
            font-weight: 600;
        }

        .form-group {
            margin-bottom: 1.5rem;
        }

        .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
        }

        .form-group input,
        .form-group textarea {
            width: 100%;
            padding: 1rem;
            border: 2px solid #eee;
            border-radius: 10px;
            font-size: 1rem;
            transition: border-color 0.3s ease;
        }

        .form-group input:focus,
        .form-group textarea:focus {
            outline: none;
            border-color: #614338;
        }

        .form-group textarea {
            resize: vertical;
            min-height: 120px;
        }

        /* Footer */
        .footer {
            background: #1a1a1a;
            color: white;
            padding: 3rem 0;
            text-align: center;
        }

        .footer-logo {
            font-size: 1.8rem;
            font-weight: 700;
            color: #614338;
            margin-bottom: 1rem;
        }

        .footer p {
            opacity: 0.7;
        }

        .footer-locations {
            display: flex;
            justify-content: center;
            gap: 4rem;
            margin-bottom: 2rem;
            flex-wrap: wrap;
        }

        .footer-location {
            text-align: left;
            min-width: 200px;
        }

        .footer-location h4 {
            font-size: 1.2rem;
            margin-bottom: 1rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        .footer-location p {
            margin-bottom: 0.5rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            opacity: 0.9;
        }

        .footer-brand {
            margin-top: 1rem;
            opacity: 0.7;
        }

        /* Animations */
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(30px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .fade-in {
            opacity: 0;
            transform: translateY(30px);
            transition: all 0.6s ease;
        }

        .fade-in.visible {
            opacity: 1;
            transform: translateY(0);
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
            .nav-menu {
                display: none;
            }

            .nav-auth {
                display: none;
            }

            .mobile-menu-btn {
                display: block;
            }

            .hero h1 {
                font-size: 2.5rem;
            }

            .hero h2 {
                font-size: 1.4rem;
            }

            .hero p {
                font-size: 1rem;
            }

            .hero-buttons {
                flex-direction: column;
                align-items: center;
                gap: 1rem;
            }

            .section-title h2 {
                font-size: 2rem;
            }

            .about-content,
            .contact-content {
                grid-template-columns: 1fr;
                gap: 2rem;
            }

            .about-text h2,
            .contact-info h2 {
                font-size: 2rem;
            }

            .services-grid,
            .testimonials-grid {
                grid-template-columns: 1fr;
                gap: 2rem;
            }

            .footer-locations {
                flex-direction: column;
                gap: 2rem;
            }
        }

        @media (max-width: 480px) {
            .container {
                padding: 0 1rem;
            }

            .hero h1 {
                font-size: 2rem;
            }

            .service-card,
            .testimonial-card {
                padding: 2rem 1.5rem;
            }

            .contact-form {
                padding: 2rem;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <a href="#home" className="logo">
            Muzukuru
          </a>
          <ul className="nav-menu">
            <li>
              <a href="#home">Home</a>
            </li>
            <li>
              <a href="#services">Services</a>
            </li>
            <li>
              <a href="#about">About</a>
            </li>
            <li>
              <a href="#testimonials">Testimonials</a>
            </li>
            <li>
              <a href="#contact">Contact</a>
            </li>
          </ul>
          {!isMuzukuruDomain && (
            <div className="nav-auth">
              <a href="/login" className="btn btn-outline">
                Login
              </a>
              <a href="/signup" className="btn btn-primary">
                Signup
              </a>
            </div>
          )}
          <button
            className="mobile-menu-btn"
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? "✕" : "☰"}
          </button>
        </div>
        <div className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`}>
          <ul>
            <li>
              <a href="#home">Home</a>
            </li>
            <li>
              <a href="#services">Services</a>
            </li>
            <li>
              <a href="#about">About</a>
            </li>
            <li>
              <a href="#testimonials">Testimonials</a>
            </li>
            <li>
              <a href="#contact">Contact</a>
            </li>
          </ul>
          {!isMuzukuruDomain && (
            <div className="nav-auth">
              <a href="/login" className="btn btn-outline">
                Login
              </a>
              <a href="/signup" className="btn btn-primary">
                Signup
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-content">
          <h1>Muzukuru</h1>
          <h2>Honoring Lives with Dignity and Compassion</h2>
          <p>
            We provide compassionate funeral services that celebrate life and
            bring comfort to families during their most difficult moments. Our
            experienced team is here to guide you through every step with care
            and respect.
          </p>
          <div className="hero-buttons">
            <a href="#services" className="btn btn-primary">
              Our Services →
            </a>
            <a href="#contact" className="btn btn-outline">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section">
        <div className="container">
          <div className="section-title fade-in">
            <h2>Our Services</h2>
            <p>
              We offer comprehensive funeral services tailored to honor your
              loved ones and support your family through this difficult time.
            </p>
          </div>
          <div className="services-grid">
            <div className="service-card fade-in">
              <div className="service-icon">♥</div>
              <h3>Traditional Funeral Services</h3>
              <p>
                Honoring cultural traditions with dignity and respect for your
                loved ones. We ensure every ceremony reflects the unique life
                being celebrated.
              </p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">🛡</div>
              <h3>Pre-Planning Services</h3>
              <p>
                Plan ahead to ease the burden on your family during difficult
                times. Our pre-planning services provide peace of mind for you
                and your loved ones.
              </p>
            </div>
            <div className="service-card fade-in">
              <div className="service-icon">🕐</div>
              <h3>24/7 Support</h3>
              <p>
                Available around the clock to provide compassionate care when
                you need it most. Our dedicated team is always ready to assist
                you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section about">
        <div className="container">
          <div className="about-content">
            <div className="about-text fade-in">
              <h2>About Muzukuru</h2>
              <p>
                For over two decades, Muzukuru has been a trusted name in
                funeral services, providing compassionate care to families
                across Zimbabwe. We understand that every life is unique and
                deserves to be celebrated with dignity and respect.
              </p>
              <p>
                Our experienced team is committed to helping you create
                meaningful tributes that honor your loved ones while providing
                the support and guidance you need during this difficult time.
              </p>
              <p>
                With deep roots in our community and a commitment to excellence,
                we have built lasting relationships based on trust, compassion,
                and professional service.
              </p>
              <a href="#contact" className="btn btn-primary">
                Learn More About Us
              </a>
            </div>
            <div className="about-image fade-in">
              <div>
                <div>Caring for Families</div>
                <div>Since 2000</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section">
        <div className="container">
          <div className="section-title fade-in">
            <h2>What Families Say</h2>
            <p>
              The trust families place in us is our greatest honor. Here are
              some words from those we've served.
            </p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card fade-in">
              <div className="stars">★★★★★</div>
              <p className="testimonial-text">
                "Muzukuru provided exceptional care during our family's most
                difficult time. Their compassion and professionalism were truly
                comforting."
              </p>
              <div className="testimonial-author">Sarah Mukamuri</div>
            </div>
            <div className="testimonial-card fade-in">
              <div className="stars">★★★★★</div>
              <p className="testimonial-text">
                "The team at Muzukuru handled everything with such dignity and
                respect. We couldn't have asked for better service."
              </p>
              <div className="testimonial-author">John Chivasa</div>
            </div>
            <div className="testimonial-card fade-in">
              <div className="stars">★★★★★</div>
              <p className="testimonial-text">
                "From planning to execution, Muzukuru made sure every detail was
                perfect. Thank you for honoring our loved one."
              </p>
              <div className="testimonial-author">Grace Nyathi</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section contact">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info fade-in">
              <h2>Contact Us</h2>
              <p>
                We're here for you 24/7. Reach out to us anytime you need our
                services or have questions about our offerings.
              </p>
              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">📞</div>
                  <div>+263 4 123 4567</div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">✉</div>
                  <div>info@muzukuru.co.zw</div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">📍</div>
                  <div>123 Main Street, Harare, Zimbabwe</div>
                </div>
              </div>
            </div>
            <div className="contact-form fade-in">
              <h3>Get In Touch</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Your Name</label>
                  <input type="text" id="name" name="name" required />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input type="tel" id="phone" name="phone" required />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input type="email" id="email" name="email" required />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea id="message" name="message" required></textarea>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: "100%" }}
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-logo">Muzukuru</div>
          <div className="footer-locations">
            <div className="footer-location">
              <h4>
                <LocationCityIcon fontSize="small" /> HARARE | ZIMBABWE
              </h4>
              <p>
                <PhoneIcon fontSize="small" /> +263 776 402 402
              </p>
              <p>
                <EmailIcon fontSize="small" /> tawanda@muzukuru.co.zw
              </p>
              <p>
                <LocationOnIcon fontSize="small" /> 46 B Mount Pleasant Drive
                Mount Pleasant, Harare, Zimbabwe
              </p>
            </div>
            <div className="footer-location">
              <h4>
                <LocationCityIcon fontSize="small" /> LUSAKA | ZAMBIA
              </h4>
              <p>
                <PhoneIcon fontSize="small" /> +260 965 806 278
              </p>
              <p>
                <EmailIcon fontSize="small" /> vincent@muzukuru.co.zw
              </p>
              <p>
                <LocationOnIcon fontSize="small" /> 6 Green Lane Kabulonga,
                Lusaka, Zambia
              </p>
            </div>
          </div>
          <p className="footer-brand">Brand Manual</p>
          <p>© 2025 Muzukuru Funeral Services. All rights reserved.</p>
        </div>
      </footer>
    </>
  );
};

export default Landing;
